import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { PAGES } from "@app/shared";
import { createQueryClient, platformRoutes, withPageRoutes } from "@axny/platform-ui";
import { parseSearch, stringifySearch } from "@axny/platform-ui/lib/search-params";
import { routeTree } from "./routeTree.gen";
import { Route as rootRoute } from "./routes/__root";
import { PAGE_ROUTES } from "./pages";
import "./register";
import "./styles.css";

// The platform's retry and staleness defaults.
const queryClient = createQueryClient();

const router = createRouter({
  // The file routes (routes/), the ordinary pages as code routes (pages.tsx, platform plan 56) and
  // the platform's own (D150): `/` to the Worklist, the section redirects, My reports and My access.
  routeTree: withPageRoutes(routeTree, [...platformRoutes(rootRoute, { landing: PAGES.worklist }), ...PAGE_ROUTES]),
  // The backend's wire format uses repeated keys for a numeric range, which a JSON
  // round-trip collapses to one value — half a shared range URL would vanish.
  parseSearch,
  stringifySearch,
});

// Without this the typed `Link to=`, `useSearch` and `navigate` APIs fall back to
// `any`, and a renamed route becomes a blank page rather than a type error.
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("#root is missing from index.html");

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
